import React, { useCallback } from 'react';
import { ShowSection } from 'framework/ui/layout';
import {
  CloseFormShowButton,
  EditFormShowButton,
  SaveFormShowButton,
  useFormShowContext,
  useFormContext,
} from 'framework/form';

import { Box, Stack, Typography } from '@mui/material';
import { Grid2 as Grid } from '@mui/material';

import { DATE_FORMAT, EMPTY_VALUE_INDICATOR, SanitizedHtml, useDateService } from 'framework/utils';
import { useShowContext } from 'framework/show';
import TaskSectionHeader from './TaskSectionHeader';
import {
  CLIENT_PORTAL_TASK_STATUS_LABELS,
  CLIENT_PORTAL_TASK_STATUSES,
  CLIENT_PORTAL_TASK_TYPE_LABELS,
} from 'domain/shared/clientPortal';
import { ClientPortalTask, ClientPortalUser } from '../../types';
import { LinkField, TextField } from 'framework/ui/field';
import CancelTask from './CancelTask';
import ClientPortalTaskForm from '../createUpdate/ClientPortalTaskForm';
import { useProjectId } from 'domain/shared/project';
import ClientPortalDocumentsContext from '../ClientPortalDocumentsContext';
import ClientPortalAttachmentsList from '../ClientPortalAttachmentsList';
import moment from 'moment/moment';
import AssigneeNameField from '../AssigneeNameField';
import { CopyToClipboardButton } from 'framework/ui/clipboard';
import { ACCESS_INFO_ACTIONS, ACCESS_INFO_SECTIONS, useProjectSecurityContext } from 'domain/shared/newProject';

type MainTaskSectionProps = {
  clients?: ClientPortalUser[];
  refetchClientPortalData: () => Promise<unknown>;
};

const MainTaskSection = ({ clients = [], refetchClientPortalData }: MainTaskSectionProps) => {
  const { data: showData } = useShowContext<ClientPortalTask>();
  const task = showData?.getData();
  const { formatDate, getToday } = useDateService();
  const projectId = useProjectId();
  const { isAccessGranted } = useProjectSecurityContext();

  const canManageClientPortal = isAccessGranted(ACCESS_INFO_SECTIONS.CLIENT_PORTAL, ACCESS_INFO_ACTIONS.MANAGE);

  const { isLoadingArtifact } = useFormContext();
  const attachments = [...(task.attachments || []), ...(task.responseAttachments || [])];

  const { isEditMode } = useFormShowContext();
  const renderActions = useCallback(() => {
    return isEditMode ? (
      <>
        <SaveFormShowButton disabled={isLoadingArtifact} />
        <CloseFormShowButton />
      </>
    ) : (
      <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={1}>
        {task.clientTaskLink && canManageClientPortal ? (
          <CopyToClipboardButton
            id="copyClientTaskLink"
            value={task.clientTaskLink}
            isContentCopyIcon
            boxSx={{ p: 1, display: 'flex', fontSize: 1.5 }}
          >
            Copy external task link
          </CopyToClipboardButton>
        ) : null}
        {task.status !== CLIENT_PORTAL_TASK_STATUSES.COMPLETED &&
          task.status !== CLIENT_PORTAL_TASK_STATUSES.CANCEL && (
            <>
              <CancelTask refetchClientPortalData={refetchClientPortalData} />
              <EditFormShowButton />
            </>
          )}
      </Box>
    );
  }, [isEditMode, isLoadingArtifact, refetchClientPortalData, task.clientTaskLink, canManageClientPortal, task.status]);

  return (
    <ShowSection
      justifyContent="space-between"
      titleId="client-portal-task-title"
      title={<TaskSectionHeader isEdit={isEditMode} data={task} actions={renderActions()} />}
    >
      {isEditMode ? (
        <ClientPortalTaskForm clients={clients} />
      ) : (
        <Stack spacing={2}>
          <Grid container spacing={1} size={{ md: 12, lg: 6 }}>
            <Grid size={6} container alignItems={'center'}>
              <Grid size={4}>
                <Typography color="textSecondary" variant="body2">
                  Status:
                </Typography>
              </Grid>
              <Grid size={8}>
                <Typography
                  color={
                    task.status === CLIENT_PORTAL_TASK_STATUSES.COMPLETED
                      ? 'success'
                      : task.status === CLIENT_PORTAL_TASK_STATUSES.CANCEL
                        ? 'error'
                        : 'textSecondary'
                  }
                >
                  {CLIENT_PORTAL_TASK_STATUS_LABELS[task.status ?? '']}
                </Typography>
              </Grid>
            </Grid>
            <Grid size={6} container alignItems={'center'}>
              <Grid size={4}>
                <Typography color="textSecondary" variant="body2">
                  Type:
                </Typography>
              </Grid>
              <Grid size={8}>
                <Typography>{CLIENT_PORTAL_TASK_TYPE_LABELS[task.type ?? '']}</Typography>
              </Grid>
            </Grid>
            <Grid size={6} container alignItems={'center'}>
              <Grid size={4}>
                <Typography color="textSecondary" variant="body2">
                  Due date:
                </Typography>
              </Grid>
              <Grid size={8}>
                <Typography
                  color={
                    task.status === CLIENT_PORTAL_TASK_STATUSES.OPEN &&
                    moment(task?.dueDate).isBefore(getToday(), 'day')
                      ? 'error.dark'
                      : 'textPrimary'
                  }
                >
                  {task.status === CLIENT_PORTAL_TASK_STATUSES.OPEN && task.dueDate
                    ? formatDate(task.dueDate, DATE_FORMAT.API_ONLY_DATE)
                    : EMPTY_VALUE_INDICATOR}
                </Typography>
              </Grid>
            </Grid>
            <Grid size={6} container alignItems={'center'}>
              <Grid size={4}>
                <Typography color="textSecondary" variant="body2">
                  Assignee:
                </Typography>
              </Grid>
              <Grid size={8}>
                <AssigneeNameField clients={clients} />
              </Grid>
            </Grid>
            {task.responseLink && (
              <Grid size={12} container alignItems={'center'}>
                <Grid size={2}>
                  <Typography color="textSecondary" variant="body2">
                    Secure link:
                  </Typography>
                </Grid>
                <Grid size={10}>
                  <LinkField source={'responseLink'} label={''} linkText={'Attached link'} />
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid container spacing={1} size={12} py={4}>
            <SanitizedHtml html={task.description || 'No description provided'} />
          </Grid>
          {task.cancelReason ? (
            <Grid size={12} container alignItems={'center'} pb={2}>
              <Grid size={2}>
                <Typography color="textSecondary" variant="body2">
                  Cancellation reason:
                </Typography>
              </Grid>
              <Grid size={10}>
                <TextField source={'cancelReason'} label={''} />
              </Grid>
            </Grid>
          ) : (
            <Grid container size={12} spacing={1}>
              {task.responseNote && (
                <Grid size={12} container alignItems={'center'}>
                  <Grid size={1}>
                    <Typography color="textSecondary" variant="body2">
                      Client response:
                    </Typography>
                  </Grid>
                  <Grid size={11}>
                    <TextField source={'responseNote'} label={''} />
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}
          {attachments.length > 0 && (
            <ClientPortalDocumentsContext projectId={projectId} taskId={task.id} attachments={attachments}>
              <ClientPortalAttachmentsList attachments={attachments} />
            </ClientPortalDocumentsContext>
          )}
        </Stack>
      )}
    </ShowSection>
  );
};
export default MainTaskSection;
